import { useEffect, useState } from "react";
import axios from "axios";
import AppShell from "@/components/layout/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, TrendingUp, AlertTriangle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPlatformRevenuePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/platform-revenue`)
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <AppShell>
      <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="admin-platform-revenue-page">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <DollarSign className="w-7 h-7 text-emerald-600" />
            Platform Revenue
          </h1>
          <p className="text-sm text-slate-500 mt-1">Total $5 application fees earned across every connected center.</p>
        </div>

        {loading && <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
        {error && <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>}

        {data && (
          <>
            <div className="grid gap-4 md:grid-cols-3">
              {[
                { label: "Month to Date", ...data.monthToDate },
                { label: "Year to Date", ...data.yearToDate },
                { label: "All Time", ...data.allTime },
              ].map((row) => (
                <Card key={row.label} className="border-emerald-100">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-slate-500 font-normal">{row.label}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-emerald-700">${row.revenue.toFixed(2)}</p>
                    <p className="text-xs text-slate-400 mt-1">{row.txns} transaction(s)</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-800">
                  <TrendingUp className="w-5 h-5" /> Revenue by Center
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.byCenter.length === 0 ? (
                  <p className="text-sm text-slate-400">No centers with paid transactions yet.</p>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {data.byCenter.map((c) => (
                      <div key={c.organizationId} className="py-2 flex items-center justify-between" data-testid={`revenue-center-${c.organizationId}`}>
                        <div>
                          <p className="font-medium text-slate-800">{c.name}</p>
                          <p className="text-xs text-slate-400">{c.txns} transactions</p>
                        </div>
                        <p className="font-semibold text-emerald-700">${c.revenue.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-amber-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="w-5 h-5" /> Failed Payments (last 30 days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.recentFailures.length === 0 ? (
                  <p className="text-sm text-slate-400">No failed payments in the last 30 days.</p>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {data.recentFailures.map((f, i) => (
                      <div key={i} className="py-2 text-sm">
                        <p className="font-medium text-slate-800">{f.centerName}</p>
                        <p className="text-xs text-slate-400">
                          {new Date(f.timestamp).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AppShell>
  );
}
